import { Link, useNavigate } from "react-router-dom";

import type { SmeCardView } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { cardPath } from "../inbox/InboxEntryRow";
import { MAX_BEADS } from "../inbox/Beads";
import { useWalk } from "./useWalk";

interface StepperProps {
  card: SmeCardView;
}

/**
 * The "next card" stepper: it moves along the hand-over. Opening the next card by this press counts as the SME's own
 * action, so the card it opens is recorded as displayed (`display_card`) once it is on the screen. The beads show where
 * the SME is in the hand-over: cards behind are filled, this card is ringed, the cards ahead are open. The order is the
 * server's walk (UI-04): waiting homes first, then Unsure, then the rest. The previous and next card, "Card n of m" and the
 * beads all read it, and the stepper never leaves the hand-over.
 */
export function Stepper({ card }: StepperProps) {
  const navigate = useNavigate();
  const { run, position, total, inHandOver, previousCardId, nextCardId } = useWalk(card);
  const here = run.findIndex((entry) => entry.element.id === card.element.id);
  // The bead of this card: where the list puts it, else the place the server gave.
  const index = here >= 0 ? here : position !== null ? position - 1 : -1;
  const start = Math.max(0, Math.min(index - Math.floor(MAX_BEADS / 2), run.length - MAX_BEADS));
  const beads = run.slice(start, start + MAX_BEADS);

  const go = (elementId: string | null | undefined) => {
    if (elementId) void navigate(cardPath(elementId), { state: { shown: true } });
  };

  return (
    <nav className="ra-stepper" aria-label="Move along the cards">
      <Button onClick={() => go(previousCardId)} disabled={!previousCardId}>
        Previous card
      </Button>
      <Button variant="primary" onClick={() => go(nextCardId)} disabled={!nextCardId}>
        Next card
      </Button>
      {position !== null && total !== null && (
        <span className="ra-stepper__where muted">
          Card {position} of {total}
          {inHandOver ? " in this hand-over" : ""}
        </span>
      )}
      {beads.length > 1 && (
        <ol className="ra-beads ra-beads--steps" aria-label="Cards in this hand-over">
          {beads.map((entry) => {
            const current = entry.element.id === card.element.id;
            const behind = run.indexOf(entry) < index;
            return (
              <li key={entry.element.id}>
                <Link
                  to={cardPath(entry.element.id)}
                  state={{ shown: true }}
                  className="ra-bead"
                  data-position={current ? "here" : behind ? "behind" : "ahead"}
                  data-unsure={entry.unsure ? "true" : undefined}
                  aria-current={current ? "step" : undefined}
                  aria-label={`${entry.element.name}${entry.unsure ? ", Unsure" : ""}`}
                />
              </li>
            );
          })}
        </ol>
      )}
      <Link to="/inbox" className="ra-stepper__back">
        Back to the inbox
      </Link>
    </nav>
  );
}
